'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';

import Desktop from './responsive/desktop';
import Mobile from './responsive/mobile';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IRedux } from '@/_store/types';
import { useSelector } from 'react-redux';
import { getCheckPointDeliveryZones } from '@/_service/zone.service';
import { useDeliveryZone } from '@/_hooks/useDeliveryZone';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { mapDesktopLog } from '@/_components/Map/mapDesktopLog';

const Delivery = ({ onClose }: { onClose: () => void }) => {
  const shipperId = useActiveBrand();
  const { active_geometries } = useSelector((state: IRedux) => state.settings);
  const { isMapModal, editingPoint, points } = useSelector(
    (state: IRedux) => state.common,
  );
  const [deliveryZone, setDeliveryZone] = useDeliveryZone();

  const [accommodation, setAccommodation] = useState<string>('');
  const [addressName, setAddressName] = useState<string>('');
  const [errorZone, setErrorZone] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [city, setCity] = useState<string>('');
  const [geometry, setGeometry] = useState<[number, number]>(active_geometries);
  const [markerCoords, setMarkerCoords] = useState<number[]>([]);

  const isDesktop = useIsDesktop();

  const mapRef = useRef<any>(null);
  const timer = useRef(setTimeout(() => {}, 3000));

  const activePoint = useMemo(() => points?.find((item) => item.isActive), []);

  const handleClear = () => {
    setAddressName('');
    setCity('');
  };

  const onAddressSelect = (e: any) => {
    setAddressName(e?.address);
    setMarkerCoords(e?.location);
    mapRef?.current?.setCenter([e?.location?.[0], e?.location?.[1]], 18, {
      duration: 300,
    });
  };

  const handleTariffGeozone = (lat: any, lon: any) => {
    getCheckPointDeliveryZones({ lat, lon }, shipperId)
      .then((data) => {
        if (data?.data?.all_count > 0 && !data?.data?.result?.length) {
          setMarkerCoords([]);
          setErrorZone(true);
        } else {
          setErrorZone(false);
        }

        const currentZone = data?.data?.result?.[0];

        if (currentZone?.id) {
          setDeliveryZone({
            id: currentZone.id,
            min_order_amount: currentZone.min_order_amount,
          });
        } else {
          setDeliveryZone(null);
        }
      })
      .finally(() => setLoading(false));
  };

  const onChange = (
    value: {
      address: string;
      city: string;
      house: string;
      location: number[];
    },
    isSelect: boolean,
  ) => {
    if (isSelect) {
      setMarkerCoords(value?.location);
    }
    setLoading(true);
    setCity(value.city);
    setAddressName(value.address);
    setAccommodation(value.house);

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      handleTariffGeozone(value?.location[0], value?.location[1]);
    }, 2000);
  };

  useEffect(() => {
    if (isMapModal) {
      if (activePoint?.location?.[0] && activePoint?.location?.[1]) {
        setGeometry(activePoint?.location);
      } else {
        setGeometry(active_geometries);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapModal, activePoint]);

  // Get details of point when editing it
  useEffect(() => {
    if (editingPoint && isMapModal) {
      setCity(editingPoint?.city);
      setAddressName(editingPoint?.address);
      if (editingPoint?.location) {
        setGeometry(editingPoint?.location);
        setMarkerCoords(editingPoint?.location);
      }
    }
  }, [isMapModal, editingPoint]);

  const commonProps = {
    city,
    mapRef,
    onClose,
    onChange,
    geometry,
    errorZone,
    isLoading,
    addressName,
    handleClear,
    markerCoords,
    deliveryZone,
    setDeliveryZone,
    accommodation,
    onAddressSelect,
    onMapBounded: (ll: number[]) => setMarkerCoords(ll),
  };

  useEffect(() => {
    if (!isDesktop || !isMapModal) return;
    mapDesktopLog('5b delivery-form-desktop', {
      addressName,
      hasGeometry: Boolean(geometry?.[0] && geometry?.[1]),
    });
  }, [isDesktop, isMapModal, addressName, geometry]);

  if (isDesktop) return <Desktop {...commonProps} />;

  return <Mobile {...commonProps} />;
};

export default Delivery;
